/* eslint-disable */

import { AstCompileError, AstCompileWarning } from "./ElfCompileErrors";
import { endLineCol, TToken, TTokenSymbol } from "./ElfTokenizer";

type TBuilderContext = {
    maxNumberOfRegisters: number;
    functionsMap: { [funcName: string]: TSyntaxTreeFunction },
    functionCallsMap: { [funcName: string]: TSyntaxTreeLabel[] },
    constantsMap: { [constName: string]: TSyntaxTreeConstant },
    constantCallsMap: { [constName: string]: TSyntaxTreeLabel[] }
}


type TSyntaxTreeTypes = 'PROGRAM' | 'FUNCTION' | 'IF' | 'WHILE' | 'STRING' | 'LIST' | 'MEMORY' | 'REGISTER' | 'NUMBER' | 'LABEL' | 'INSTRUCTION' | 'DEBUGPOINT' | 'EXIT' | 'BREAK' | 'TERMINATE' | 'CONTINUE' | 'CONSTANT';
type TSyntaxTreeBase<T extends TSyntaxTreeTypes> = { 
    __todo_filePath?: never /*when we want to include multiple files as compilation we need to know origin! (file[line:col] error resolving / highlighting)*/, 
    __todo__endPos?: never /* once all nodes have endpos (for debugger) this can be set here at base level */, 
    line: number, column: number, type: T };

export type TSyntaxTreeProgram = TSyntaxTreeBase<'PROGRAM'> & {
    blockOf: 'ROOT' | 'FUNCTION' | 'WHILE' | 'IF' | 'ELSE',
    level: number, sequence: TSyntaxTree[],
    canUseLoopControlStatements: boolean, canUseFunctionControlStatements: boolean
};

export type TSyntaxTreePos = { line: number, column: number } | { eof: true, line?: never, column?: never }
export type TSyntaxTreeFunction = TSyntaxTreeBase<'FUNCTION'> & { name: string, body: TSyntaxTreeProgram, label: TSyntaxTreeLabel }
export type TSyntaxTreeIf = TSyntaxTreeBase<'IF'> & {/* endPos is for debugger. */ endPos: TSyntaxTreePos, expression: TGroupSyntaxTreeExpression, true: TSyntaxTreeProgram, false: TSyntaxTreeProgram | null }
export type TSyntaxTreeWhile = TSyntaxTreeBase<'WHILE'> & { /* endPos is for debugger. */ endPos: TSyntaxTreePos, expression: TGroupSyntaxTreeExpression, true: TSyntaxTreeProgram }
export type TSyntaxTreeString = TSyntaxTreeBase<'STRING'> & { value: string };
export type TSyntaxTreeList = TSyntaxTreeBase<'LIST'> & { /* endPos is for debugger. */ endPos: TSyntaxTreePos, list: TSyntaxTree[] };
export type TSyntaxTreeMemory = TSyntaxTreeBase<'MEMORY'> & {  /* endPos is for debugger. */ endPos: TSyntaxTreePos, innerExpression: TSyntaxTree };
export type TSyntaxTreeRegister = TSyntaxTreeBase<'REGISTER'> & { /* endPos is for debugger. */ endPos: TSyntaxTreePos, registerNumber: number }
export type TSyntaxTreeNumber = TSyntaxTreeBase<'NUMBER'> & { /* endPos is for debugger. */ endPos: TSyntaxTreePos, value: number, token: string };
export type TSyntaxTreeLabel = TSyntaxTreeBase<'LABEL'> & { /* endPos is for debugger. */ endPos: TSyntaxTreePos, token: string; lastLine: number; lastColumn: number, valueIfConstant?: number };
export type TSyntaxTreeInstruction = TSyntaxTreeBase<'INSTRUCTION'> & {
    /* endPos is for debugger. */ endPos: TSyntaxTreePos, instructionName: string; args: TSyntaxTree[]
};
export type TSyntaxTreeDebugPoint = TSyntaxTreeBase<'DEBUGPOINT'> & { /* endPos is for debugger. */ endPos: TSyntaxTreePos };
export type TSyntaxTreeExit = TSyntaxTreeBase<'EXIT'> & { /* endPos is for debugger. */ endPos: TSyntaxTreePos, expression: TGroupSyntaxTreeExpression };
export type TSyntaxTreeTerminate = TSyntaxTreeBase<'TERMINATE'> & { /* endPos is for debugger. */ endPos: TSyntaxTreePos, expression: TGroupSyntaxTreeExpression };
export type TSyntaxTreeBreak = TSyntaxTreeBase<'BREAK'> & { /* endPos is for debugger. */ endPos: TSyntaxTreePos };
export type TSyntaxTreeContinue = TSyntaxTreeBase<'CONTINUE'> & {/* endPos is for debugger. */ endPos: TSyntaxTreePos };
export type TSyntaxTreeConstant = TSyntaxTreeBase<'CONSTANT'> & { name: string, value: TSyntaxTreeNumber };

export type TSyntaxTree = TSyntaxTreeProgram | TSyntaxTreeFunction | TSyntaxTreeIf |
    TSyntaxTreeWhile | TSyntaxTreeString | TSyntaxTreeList |
    TSyntaxTreeMemory | TSyntaxTreeRegister | TSyntaxTreeNumber |
    TSyntaxTreeLabel | TSyntaxTreeInstruction | TSyntaxTreeDebugPoint | TSyntaxTreeExit | TSyntaxTreeBreak | TSyntaxTreeContinue | TSyntaxTreeConstant | TSyntaxTreeTerminate;

export type TGroupSyntaxTreeNumericExpression = TSyntaxTreeNumber | TSyntaxTreeRegister | TSyntaxTreeMemory | TSyntaxTreeLabel;
export type TGroupSyntaxTreeExpression = TGroupSyntaxTreeNumericExpression | TSyntaxTreeInstruction;


type TElfPrimitiveTypes = TGroupSyntaxTreeNumericExpression['type'];
type TElfKeywordTypes = Exclude<TSyntaxTreeTypes, TElfPrimitiveTypes | 'STRING' | 'PROGRAM' | 'LIST' | 'INSTRUCTION'>;
type TElfInstructionTypes = Exclude<string, TSyntaxTreeTypes>;

type TElfSyntaxTreePrimitiveTypePartialMapping<T> = Partial<Record<TElfPrimitiveTypes, T>>;
type TElfSyntaxTreeKeywordsTypePartialMapping<T> = Partial<Record<TElfKeywordTypes, T>>;
type TElfSyntaxTreeInstructionTypePartialMapping<T> = Partial<Record<TElfInstructionTypes, T>>;


type TElfLanguageBuildKeywordContext = { parseFn(token: TToken, tokens: TToken[], ast: TSyntaxTreeProgram, ctx: TSyntaxTreeBuilderContext): void; }
type TElfLanguageBuildInstructionContext = { args: number } | { parseArgsFn(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[]; }
type TElfLanguageTransformKeywordContext = (tree: TSyntaxTree, opts: { debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) => string;
type TElfLanguageTransformInstructionContext = (tree: TSyntaxTreeInstruction, opts: { debug: boolean, astBuildContext: TSyntaxTreeBuilderContext, isParentControlStmt?: boolean }) => string;
type TElfLanguageTransformPrimitiveContext = (tree: TGroupSyntaxTreeNumericExpression, opts: { debug: boolean, astBuildContext: TSyntaxTreeBuilderContext, isParentControlStmt?: boolean }) => string;
export type TElfLanguagePrimitiveMetadata = {
    name: string;
    cyclesDescription: string;
    description: string;
    example: string;
};
export type TElfLanguageKeywordsMetadata = {
    cyclesDescription: string;
    modifiesAccumulator: boolean;
    arguments: { type: string, argToken: string, argName: string, argDescription: string }[][], description: string; example: string;
};
export type TElfLanguageInstructionsMetadata = {
    cyclesDescription: string;
    modifiesAccumulator: boolean;
    returnsAccumulator: boolean;
    arguments: { type: string, argToken: string, argName: string, argDescription: string }[][], description: string; example: string;
};

type TElfLanguageContext = {
    meta: {
        batches: {
            list: {
                name: string;
                cyclesDescription: string;
                description: string;
            },
            string: {
                name: string;
                cyclesDescription: string;
                description: string;
            }
        }
        references: {
            name: string;
            cyclesDescription: string;
            description: string;
        }
    },
    keywords: TElfSyntaxTreeKeywordsTypePartialMapping<{
        metadata: TElfLanguageKeywordsMetadata,
        build: TElfLanguageBuildKeywordContext,
        transform: TElfLanguageTransformKeywordContext
    }>,
    instructions: TElfSyntaxTreeInstructionTypePartialMapping<{
        metadata: TElfLanguageInstructionsMetadata,
        build: TElfLanguageBuildInstructionContext,
        transform: TElfLanguageTransformInstructionContext
    }>,
    primitives: TElfSyntaxTreePrimitiveTypePartialMapping<{
        metadata: TElfLanguagePrimitiveMetadata,
        build?: never /*currently internal only!*/,
        transform: TElfLanguageTransformPrimitiveContext
    }>
}

export type TSyntaxTreeBuilderContext = {
    builderContext: TBuilderContext;
    languageContext: TElfLanguageContext;
}

// given the NEXT token (if its there) return the position where the NEXT token STARTS
// this will give us current "END" position, before NEXT token starts.
export function peekForEndPosition(tokens: TToken[]): TSyntaxTreePos {
    const nextTokenPeek = tokens[0];

    const endPos: TSyntaxTreePos = nextTokenPeek ? {
        line: nextTokenPeek.line,
        column: nextTokenPeek.column
    } : { eof: true };

    return endPos;
}

export function isConsumableSymbolOf(token: TToken | undefined, symbol: string): boolean {
    return token?.type === 'SYMBOL' && token.token === symbol;
}

export function consumeString(tokens: TToken[]): TSyntaxTreeString {
    const stringToken = tokens[0];
    if (!stringToken) {
        throw new AstCompileError(`Pričakovan niz znakov,  vendar ni več znakov v programu.`, { eof: true }, { eof: true })
    }
    if (stringToken.type !== 'STRING') {
        throw new AstCompileError(`Pričakovan niz znakov, vendar dobil "${stringToken.token}"`, stringToken, endLineCol(stringToken));
    }
    consumeCurrentToken(tokens);

    return { type: 'STRING', value: stringToken.value, line: stringToken.line, column: stringToken.column };
}

export function consumeList(tokens: TToken[], cbEntry: (tokens: TToken[]) => TSyntaxTree): TSyntaxTreeList {
    const blockStartToken = tokens[0]!;
    consumeBlockStart(tokens);
    consumeIfSpaceElseIgnore(tokens);
    const list = [];
    while (true) {
        if (isConsumableSymbolOf(tokens[0], "}")) {
            break;
        }
        list.push(cbEntry(tokens));
        if (isConsumableSymbolOf(tokens[0], "}")) {
            break;
        } else {
            consumeSpace(tokens);
        }
    }
    const blockEndToken = consumeBlockEnd(tokens);
    if (!list.length) {
        throw new AstCompileError(`Pričakovan vsaj en argument v seznamu, vendar seznam je bil prazen`, blockStartToken, blockEndToken);
    }
    return { type: 'LIST', list, line: blockStartToken.line, column: blockStartToken.column, endPos: peekForEndPosition(tokens) };
}

export function consumeRegisterOrMemory(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTreeMemory | TSyntaxTreeRegister {
    const next = tokens[0];
    if (!next) {
        throw new AstCompileError(`Pričakovan register ali pomnilnik, vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }

    if (next.type === 'REGISTER') {
        return consumeRegister(tokens, ctx);;
    }
    if (isConsumableSymbolOf(next, '[')) {
        return consumeMemory(tokens, ctx);
    }

    throw new AstCompileError(`Pričakovan register ali pomnilnik, vendar dobil "${next.token}"`, next, endLineCol(next));
}

export function consumeCurrentToken(tokens: TToken[]): TToken | undefined {
    return tokens.shift();
}

export function consumeSpace(tokens: TToken[]): void {
    const next = tokens.shift();
    if (!next) {
        throw new AstCompileError(`Pričakovan presledek, vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }
    if (next.type !== 'SPACE') {
        throw new AstCompileError(`Pričakovan presledek, vendar dobil ${next.token}`, next, endLineCol(next));
    }
}

export function consumeLabel(tokens: TToken[]): TSyntaxTreeLabel {
    const next = tokens.shift();
    if (!next) {
        throw new AstCompileError(`Pričakovana uporabiško definirana labela, vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }
    if (next.type !== 'LABEL') {
        throw new AstCompileError(`Pričakovana uporabiško definirana labela (niz znakov), vendar dobil ${next.token}`, next, endLineCol(next));
    }

    const endPos = endLineCol(next);
    return { type: 'LABEL', token: next.token, line: next.line, column: next.column, lastLine: endPos.line, lastColumn: endPos.column, endPos: peekForEndPosition(tokens) };
}

export function consumeConstantLabel(tokens: TToken[], { builderContext }: TSyntaxTreeBuilderContext): TSyntaxTreeLabel {
    const labelItem = consumeLabel(tokens);
    (builderContext.constantCallsMap[labelItem.token] ??= []).push(labelItem);
    return labelItem;
}

export function consumeBlockStart(tokens: TToken[]): TToken {
    const next = tokens.shift();
    if (!next) {
        throw new AstCompileError(`Pričakovan začetek bloka "{", vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }
    if (next.type !== 'SYMBOL' || next.token !== '{') {
        throw new AstCompileError(`Pričakovan začetek bloka "{", vendar dobil "${next.token}"`, next, endLineCol(next));
    }

    return next;
}

export function consumeBlockEnd(tokens: TToken[]): TTokenSymbol {
    const next = tokens.shift();
    if (!next) {
        throw new AstCompileError(`Pričakovan konec bloka "}", vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }
    if (next.type !== 'SYMBOL' || next.token !== '}') {
        throw new AstCompileError(`Pričakovan konec bloka "}", vendar dobil "${next.token}"`, next, endLineCol(next));
    }

    return next;
}

export function consumeIfSpaceElseIgnore(tokens: TToken[]): void {
    if (!tokens.length) return;
    if (tokens[0]!.type === 'SPACE') tokens.shift();
}

export function consumeNumber(tokens: TToken[]): TSyntaxTreeNumber {
    const next = tokens.shift();
    if (!next) {
        throw new AstCompileError(`Pričakovano število, vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }

    if (next.type !== 'NUMBER') {
        throw new AstCompileError(`Pričakovano število, vendar dobil "${next.token}"`, next, endLineCol(next));
    }

    const numberValue = Number(next.token);
    // It should be noted to the user that underlaying engine is ECMAScript Float64 for numbers! and all quirks associated with number ops (inf, nan,...)
    return { type: 'NUMBER', token: next.token, value: numberValue, line: next.line, column: next.column, endPos: peekForEndPosition(tokens) };
}

export function consumeAsciiNumber(tokens: TToken[]): TSyntaxTreeNumber {
    const next = tokens.shift();
    if (!next) {
        throw new AstCompileError(`Pričakovan ascii številčni mapirnik "#", vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }

    if (next.type !== 'ASCII') {
        throw new AstCompileError(`Pričakovan ascii številčni mapirnik "#", vendar dobil "${next.token}"`, next, endLineCol(next));
    }

    const charCode = next.charCode;
    return { type: 'NUMBER', value: charCode, token: next.token, line: next.line, column: next.column, endPos: peekForEndPosition(tokens) };
}

export function consumeRegister(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTreeRegister {
    const next = tokens.shift();
    if (!next) {
        throw new AstCompileError(`Pričakovan register, vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }

    if (next.type !== 'REGISTER') {
        throw new AstCompileError(`Pričakovan register, vendar dobil "${next.token}"`, next, endLineCol(next));
    }

    const registerNumber = Number(next.value);
    if (registerNumber > ctx.builderContext.maxNumberOfRegisters) {
        throw new AstCompileError(`Sledeč register "${next.token}" ne obstaja - izberi vrednosti iz razpona: [1,${ctx.builderContext.maxNumberOfRegisters}]`, next, endLineCol(next));
    }
    return { type: 'REGISTER', registerNumber, line: next.line, column: next.column, endPos: peekForEndPosition(tokens) };
}

export function consumeMemory(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTreeMemory {
    const peek = consumeCurrentToken(tokens);
    if (!peek) {
        throw new AstCompileError(`Pričakoval začetek dostopa do pomnilnika "[", vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }
    if (peek.type !== "SYMBOL" || peek.token !== "[") {
        throw new AstCompileError(`Pričakoval začetek dostopa do pomnilnika "[", vendar dobil "${peek.token}"`, peek, endLineCol(peek));
    }
    consumeIfSpaceElseIgnore(tokens);
    const peekAfterMemory = tokens[0];
    if (!peekAfterMemory) {
        throw new AstCompileError(`Pričakovan izraz pri dostopu do pomnilnika, vendar ni več znakov v programu.`, peek, { eof: true });
    }
    // [ expre..
    const innerExpression = consumeExpression(tokens, ctx)
    //   ..ssion ]
    consumeIfSpaceElseIgnore(tokens);
    const endMemoryToken = tokens.shift();
    if (!endMemoryToken) {
        throw new AstCompileError(`Pričakoval zaključek dostopa do pomnilnika "]", vendar ni več znakov v programu.`, peek, { eof: true });
    }
    if (endMemoryToken.type !== 'SYMBOL' || endMemoryToken.token !== ']') {
        throw new AstCompileError(`Pričakoval zaključek dostopa do pomnilnika "]" vendar dobil "${endMemoryToken.token}"`, peek, endLineCol(endMemoryToken));
    }
    return {
        type: 'MEMORY',
        innerExpression,
        line: peek.line,
        column: peek.column,
        endPos: peekForEndPosition(tokens)
    };
}

// THIS SHOULD ONLY BE USED IF PARENT LAYER OF EXPRESSION SHOULD GUARANTEE A NUMERIC EXPRESSION
// meaning this should not be start of the program, but rather a return value that something can use.
export function consumeNumericExpression(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TGroupSyntaxTreeNumericExpression {
    const peek = tokens[0];
    if (!peek) {
        throw new AstCompileError(`Pričakovan numerični izraz, vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }
    switch (peek.type) {
        case 'NUMBER': {
            return consumeNumber(tokens);
        }
        case 'LABEL': {
            return consumeConstantLabel(tokens, ctx);
        }
        case 'REGISTER': {
            return consumeRegister(tokens, ctx);
        }
        case 'ASCII': {
            return consumeAsciiNumber(tokens);
        }
        case 'SYMBOL': {
            switch (peek.token) {
                case '[': return consumeMemory(tokens, ctx);
                default:
                    throw new AstCompileError(`Sledeč simbol "${peek.token}" ni podprt v numeričnem izrazu`, peek, endLineCol(peek));
            }
        }
        default: {
            throw new AstCompileError(`Sledeč simbol "${peek.token}" ni podprt v numeričnem izrazu`, peek, endLineCol(peek));
        }
    }
}

export function consumeKeywordExpression(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTreeInstruction {
    const token = tokens.shift();
    if (!token) {
        throw new AstCompileError(`Pričakovana rezervirana inštrukcija, vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }
    if (token.type !== 'KEYWORD') {
        throw new AstCompileError(`Pričakovana rezerviran inštrukcija, vendar dobil "${token.token}"`, token, endLineCol(token));
    }
    const builtinFunction = ctx.languageContext.instructions[token.token as keyof typeof ctx.languageContext.instructions]?.build;
    if (!builtinFunction) {
        throw new AstCompileError(`Pričakovana rezervirana inštrukcija, vendar dobil: "${token.token}" (si morda zamešal uporabo ključnih besed in inštrukcij?)`, token, endLineCol(token));
    }


    const instructionArgs: TSyntaxTreeInstruction['args'] = [];

    if ('parseArgsFn' in builtinFunction) {
        instructionArgs.push(...builtinFunction.parseArgsFn(tokens, ctx));
    } else {
        for (let i = 0; i < builtinFunction.args; i++) {
            consumeSpace(tokens);
            const arg = consumeExpression(tokens, ctx);
            instructionArgs.push(arg);
        }
    }

    const instruction: TSyntaxTreeInstruction = {
        type: 'INSTRUCTION',
        instructionName: token.token,
        args: instructionArgs,
        line: token.line, column: token.column,
        endPos: peekForEndPosition(tokens),
    };

    return instruction;
}

export function consumeExpression(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TGroupSyntaxTreeExpression {
    const peek = tokens[0];
    if (!peek) {
        throw new AstCompileError(`Pričakovan izraz, vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }
    switch (peek.type) {
        case 'NUMBER':
        case 'SYMBOL':
        case 'LABEL':
        case 'ASCII':
        case 'REGISTER':
            return consumeNumericExpression(tokens, ctx);
        case 'KEYWORD':
            return consumeKeywordExpression(tokens, ctx);
        default:
            throw new AstCompileError(`Sledeč simbol "${peek.token}" ni podprt izraz`, peek, endLineCol(peek));
    }
}


export function parseNested(ast: TSyntaxTreeProgram, tokens: TToken[], ctx: TSyntaxTreeBuilderContext): void {
    let lastTokensCount = null;
    while (tokens.length && lastTokensCount !== tokens.length) {
        lastTokensCount = tokens.length;
        const token = tokens[0]!;
        switch (token.type) {
            case 'SPACE': {
                consumeCurrentToken(tokens);
                break;
            }
            case 'KEYWORD': {
                const keyword = ctx.languageContext.keywords[token.token as keyof typeof ctx.languageContext.keywords];
                if (keyword) {
                    keyword.build.parseFn(token, tokens, ast, ctx);
                } else {
                    ast.sequence.push(consumeKeywordExpression(tokens, ctx));
                }
                break;
            }
            default: {
                // let other nestedAST parser handle.
                break;
            }
        }
    }
}

function tokensToAST(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree {
    const firstLineColumn = tokens[0] ?? { line: 1, column: 1 };
    const ast: TSyntaxTreeProgram = {
        type: 'PROGRAM', blockOf: 'ROOT', canUseLoopControlStatements: false, canUseFunctionControlStatements: false, level: 0, sequence: [],
        line: firstLineColumn.line, column: firstLineColumn.column
    };
    parseNested(ast, tokens, ctx);
    consumeIfSpaceElseIgnore(tokens);
    // check if whole program has been succesfully consumed.
    if (tokens.length) {
        const token = tokens.shift()!;
        throw new AstCompileError(`Neveljaven stavek`, token, { eof: true });
    }
    return ast;
}

export function buildAST(tokensIn: TToken[], ctx: TSyntaxTreeBuilderContext): { ast: TSyntaxTree | null, warnings: AstCompileWarning[], errors: AstCompileError[] } {
    const tokens = [...tokensIn];

    const warnings: AstCompileWarning[] = [];
    const errors: AstCompileError[] = [];
    try {
        const ast = tokensToAST(tokens, ctx);
        // statically analyzers here (after AST is created)
        /// Check for undefined function calls
        for (const [calledFunctionName, usages] of Object.entries(ctx.builderContext.functionCallsMap)) {
            const [calledFunction] = usages;
            const functionDefinition = ctx.builderContext.functionsMap[calledFunctionName];
            if (!functionDefinition) {
                errors.push(new AstCompileError(`Klicana funkcija "${calledFunctionName}" ni definirana`, calledFunction, { line: calledFunction.line, column: calledFunction.column + calledFunction.token.length - 1 }));
            }
        }
        /// Check for undefined constant accesses
        ///// Also assign values to constants if they are defined!
        for (const [calledConstantName, usages] of Object.entries(ctx.builderContext.constantCallsMap)) {
            const [calledConstant] = usages;
            const constantDefinition = ctx.builderContext.constantsMap[calledConstantName];
            if (!constantDefinition) {
                errors.push(new AstCompileError(`Konstanta "${calledConstantName}" ni definirana`, calledConstant, { line: calledConstant.line, column: calledConstant.column + calledConstant.token.length - 1 }));
            } else {
                usages.forEach(constant => constant.valueIfConstant = constantDefinition.value.value);
            }
        }

        // Check for unused functions
        for (const [definedFunctionName, functionDefinition] of Object.entries(ctx.builderContext.functionsMap)) {
            if (!ctx.builderContext.functionCallsMap[definedFunctionName]) {
                console.log('function label', functionDefinition.label);
                warnings.push(new AstCompileWarning(`Funkcija ${functionDefinition.name} se ne uporablja v programu.`, functionDefinition, { line: functionDefinition.label.line, column: functionDefinition.label.column + functionDefinition.label.token.length - 1 }))
            }
        }

        // Check for unused constants
        for (const [definedConstantName, constantDefinition] of Object.entries(ctx.builderContext.constantsMap)) {
            if (!ctx.builderContext.constantCallsMap[definedConstantName]) {
                warnings.push(new AstCompileWarning(`Konstanta ${constantDefinition.name} se ne uporablja v programu.`, constantDefinition, { line: constantDefinition.value.line, column: constantDefinition.value.column + constantDefinition.value.token.length - 1 }))
            }
        }
        return { ast: errors.length ? null : ast, warnings, errors };
    } catch (err) {
        if (err instanceof AstCompileError) {
            return { ast: null, errors: [err, ...errors], warnings }
        }
        throw err;
    }
}